// Rules of the candidates import shared by the browser and the server.

import type { ImportProfileType } from "./profiles/importProfiles";

/** Same normalisation as the database: the e-mail is the identity of a person. */
export const normalizeImportEmail = (value: unknown): string | null => {
  const email = typeof value === "string" ? value.trim().toLowerCase() : "";
  return email === "" ? null : email;
};

/** What identifies a person in a preview row (the rows are keyed by column header). */
export type CandidateIdentityRowType = {
  first_name: string;
  last_name1: string;
  last_name2: string;
  national_id: string;
  email: string;
};

export const candidateIdentityRows = (
  rows: Record<string, unknown>[],
  profile: ImportProfileType,
  languageCode: string,
): CandidateIdentityRowType[] => {
  const header = (key: string) => {
    const column = profile.columns[key];
    return column?.fieldHeader?.[languageCode] || column?.fieldHeader?.en || key;
  };
  const text = (row: Record<string, unknown>, key: string) => String(row[header(key)] ?? "").trim();

  return rows.map((row) => ({
    first_name: text(row, "first_name"),
    last_name1: text(row, "last_name1"),
    last_name2: text(row, "last_name2"),
    national_id: text(row, "national_id"),
    email: normalizeImportEmail(text(row, "email")) ?? "",
  }));
};

/** Text of a warning of the database about a row of the preview. */
export const importWarningText = (warning: string, detail: string | null): string => {
  const who = detail ?? "otra persona";
  switch (warning) {
    case "other_name":
      return `El correo es de ${who} en tu empresa, con otro nombre. Si importas la fila, se actualizan sus datos con los del archivo.`;
    case "name_duplicate":
      return `Posible duplicado de ${who}: mismo nombre completo, sin correo ni identificación que coincidan. Si importas la fila, se crea otra persona.`;
    case "email_repeated":
      return `El mismo correo está en la ${who}. Se actualizará esa persona con esta fila.`;
    case "erased":
      return "Sus datos fueron borrados en tu empresa. La fila no se importará.";
    default:
      return warning;
  }
};

/** Warnings the user can accept to import the row anyway. */
export const isForceableWarning = (warning: string): boolean =>
  warning === "other_name" || warning === "name_duplicate";
